import { useCallback, useEffect, useState } from 'react'

import { CloseButton, Modal, TextInput } from '@mantine/core'

import { useMutation } from '@redwoodjs/web'

import DNButton from 'src/components/Buttons/Button/DNButton'
import { toast } from 'src/components/Overlays/Toast/Toast'
import {
  CREATE_CUSTOM_STEP,
  UPDATE_CUSTOM_STEP,
} from 'src/graphql/customSteps.graphql'

interface CustomStepModalProps {
  customStep?: any
  edit: boolean
  isOpen: boolean
  onClose: () => void
  refetchQueries: any[]
}

const CustomStepModal = ({
  customStep,
  edit,
  isOpen,
  onClose,
  refetchQueries,
}: CustomStepModalProps) => {
  const [label, setLabel] = useState('')
  const isValid = label.trim() !== ''

  // Sync the form when the modal opens (prefill on edit, clear on create)
  useEffect(() => {
    if (isOpen) {
      setLabel(edit && customStep ? customStep.label ?? '' : '')
    }
  }, [isOpen, edit, customStep])

  const [createCustomStep, { loading: createLoading }] = useMutation(
    CREATE_CUSTOM_STEP,
    {
      refetchQueries,
      onCompleted: () => {
        onClose()
        toast('Custom step created', 'success')
      },
      onError: (error) =>
        toast(error.message || 'Error creating custom step', 'error'),
    }
  )

  const [updateCustomStep, { loading: updateLoading }] = useMutation(
    UPDATE_CUSTOM_STEP,
    {
      refetchQueries,
      onCompleted: () => {
        onClose()
        toast('Custom step updated', 'success')
      },
      onError: (error) =>
        toast(error.message || 'Error updating custom step', 'error'),
    }
  )

  const handleSubmit = useCallback(() => {
    if (!isValid) return
    const trimmed = label.trim()

    if (edit && customStep?.customStepId) {
      updateCustomStep({
        variables: { id: customStep.customStepId, input: { label: trimmed } },
      })
    } else {
      createCustomStep({ variables: { input: { label: trimmed } } })
    }
  }, [isValid, label, edit, customStep, createCustomStep, updateCustomStep])

  return (
    <Modal
      opened={isOpen}
      onClose={onClose}
      yOffset={120}
      overlayProps={{ backgroundOpacity: 0.2, blur: 5 }}
      size="40%"
      withCloseButton={false}
      classNames={{ body: 'flex flex-col gap-4' }}
    >
      <div className="flex flex-row items-start justify-between">
        <div className="text-lg font-semibold text-doubleNickel-gray-900">
          {edit ? 'Edit custom step' : 'Create custom step'}
        </div>
        <CloseButton onClick={onClose} />
      </div>

      <TextInput
        label="Label"
        placeholder="e.g. Equipment Inspection"
        value={label}
        onChange={(event) => setLabel(event.currentTarget.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') handleSubmit()
        }}
        data-testid="custom-step-label-input"
      />

      <div className="flex flex-row justify-end">
        <DNButton
          variant="primary"
          onClick={handleSubmit}
          loading={edit ? updateLoading : createLoading}
          disabled={!isValid}
        >
          {edit ? 'Update' : 'Create'}
        </DNButton>
      </div>
    </Modal>
  )
}

export default CustomStepModal
